/**
 * 契約一覧・契約詳細APIクライアント（FKV3-13045）
 * バックエンドの /credit/contracts を呼び出す。月は `YYYY-MM`、数値は10進の文字列
 */

import { ApiError, apiDelete, apiGet, apiPost, apiPut } from './client';

export type PricingPlan = 'Credit' | 'FixedFee';
export type ContractTable = 'pricing-plans' | 'data-connect-credits';
/** データコネクトのクレジット数の区分 */
export type Category = 'scheduled' | 'active' | 'past';

export interface PlanValues {
  start_month: string;
  end_month: string | null;
  pricing_plan: PricingPlan;
  monthly_contract_credit: string | null;
  credit_unit_price: string | null;
  monthly_fee: string | null;
}

export interface DataConnectValues {
  start_month: string;
  end_month: string | null;
  monthly_credit: string;
}

export interface CompanySummary {
  id: string;
  name: string;
  favorite: boolean;
  /** 指定した月の契約料金プラン */
  pricing_plan: PlanValues | null;
  /** 指定した月のデータコネクトのクレジット数 */
  data_connect_credit: DataConnectValues | null;
}

export interface ContractList {
  /** 当月（日本時間）。ブラウザの時計でなくこれを使う */
  current_month: string;
  month: string;
  /** 企業IDの順 */
  companies: CompanySummary[];
}

export interface RowMeta {
  /** 編集・削除・追加で終了月を合わせるときに送り返す */
  version: string;
  updated_at: string;
  updated_by: string;
}

export type PlanRow = PlanValues & RowMeta;
export type DataConnectRow = DataConnectValues & RowMeta & { category: Category };

export interface FaceValue {
  effective_dt: string;
  credit_face_value: number;
}

export interface ContractDetail {
  current_month: string;
  company: { id: string; name: string };
  /** 開始月の新しい順 */
  pricing_plans: PlanRow[];
  data_connect_credits: DataConnectRow[];
  /** 適用開始日の古い順。予定の値も含む */
  credit_face_values: FaceValue[];
}

/** 保存する行の入力値。テーブルで使わない欄は送らない */
export interface RowInput {
  start_month: string;
  end_month: string;
  pricing_plan?: PricingPlan | '';
  monthly_contract_credit?: string;
  credit_unit_price?: string;
  monthly_fee?: string;
  monthly_credit?: string;
}

export interface RowRef {
  start_month: string;
  version: string;
}

export interface Violation {
  /** 入力欄。null は入力欄に属さないエラー */
  field: keyof RowInput | null;
  message: string;
}

const base = '/credit/contracts';
const companyPath = (companyId: string) => `${base}/${encodeURIComponent(companyId)}`;

export function getContracts(month: string | null, authHeaders?: Record<string, string>) {
  const query = month ? `?${new URLSearchParams({ month })}` : '';
  return apiGet<ContractList>(`${base}${query}`, authHeaders);
}

export function getContract(companyId: string, authHeaders?: Record<string, string>) {
  return apiGet<ContractDetail>(companyPath(companyId), authHeaders);
}

export function addFavorite(companyId: string, authHeaders?: Record<string, string>) {
  return apiPut<void>(`${companyPath(companyId)}/favorite`, {}, authHeaders);
}

export function removeFavorite(companyId: string, authHeaders?: Record<string, string>) {
  return apiDelete<void>(`${companyPath(companyId)}/favorite`, authHeaders);
}

export function createRow(
  companyId: string,
  table: ContractTable,
  request: { current_month: string; row: RowInput; adjust: RowRef | null },
  authHeaders?: Record<string, string>,
) {
  return apiPost<{ start_month: string }>(
    `${companyPath(companyId)}/${table}`,
    request,
    authHeaders,
  );
}

export function updateRow(
  companyId: string,
  table: ContractTable,
  originalStart: string,
  request: { current_month: string; version: string; row: RowInput },
  authHeaders?: Record<string, string>,
) {
  return apiPut<{ start_month: string }>(
    `${companyPath(companyId)}/${table}/${originalStart}`,
    request,
    authHeaders,
  );
}

export function deleteRow(
  companyId: string,
  table: ContractTable,
  start: string,
  query: { current_month: string; version: string },
  authHeaders?: Record<string, string>,
) {
  return apiDelete<void>(
    `${companyPath(companyId)}/${table}/${start}?${new URLSearchParams(query)}`,
    authHeaders,
  );
}

/** サーバーが返した失敗の種類。`NETWORK` はレスポンスが返らなかったとき */
export type FailureCode =
  | 'INVALID_MONTH'
  | 'COMPANY_NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'ROW_CHANGED'
  | 'MONTH_CHANGED'
  | 'NOT_ALLOWED'
  | 'USER_EMAIL_MISSING'
  | 'CREDIT_DB_UNAVAILABLE'
  | 'COMMON_DB_UNAVAILABLE'
  | 'SAVE_BUSY'
  | 'NETWORK'
  | 'OTHER';

export interface Failure {
  code: FailureCode;
  /** サーバーが返したメッセージ（なければ HTTP の状態） */
  message: string;
  violations: Violation[];
}

const knownCodes: FailureCode[] = [
  'INVALID_MONTH',
  'COMPANY_NOT_FOUND',
  'VALIDATION_ERROR',
  'ROW_CHANGED',
  'MONTH_CHANGED',
  'NOT_ALLOWED',
  'USER_EMAIL_MISSING',
  'CREDIT_DB_UNAVAILABLE',
  'COMMON_DB_UNAVAILABLE',
  'SAVE_BUSY',
];

/** API の失敗を、画面が表示を分ける種類にする */
export function toFailure(error: unknown): Failure {
  if (error instanceof ApiError) {
    if (error.status === 0) {
      return { code: 'NETWORK', message: error.message, violations: [] };
    }
    const data = (error as ApiError & { data?: Record<string, unknown> }).data ?? {};
    const code = knownCodes.find((c) => c === data.code) ?? 'OTHER';
    const message =
      typeof data.error === 'string' && data.error ? data.error : `HTTP ${error.status}`;
    const violations = Array.isArray(data.violations) ? (data.violations as Violation[]) : [];
    return { code, message, violations };
  }
  return {
    code: 'NETWORK',
    message: error instanceof Error ? error.message : String(error),
    violations: [],
  };
}
